import TemplatesApi from './Templates.api';

/**
 * Simple debounce utility for delaying function execution.
 * @param {Function} func - The function to debounce.
 * @param {number} delay - The delay in milliseconds.
 * @returns {Function} The debounced function.
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Fetches topics and templates from the API.
 * @returns {Promise<{topics: Array, templates: Array}>} The fetched data.
 */
export const fetchFiltersAndTemplates = async () => {
  const [topicsResponse, templatesResponse] = await Promise.all([
    TemplatesApi.getTopics(),
    TemplatesApi.getTemplates(),
  ]);
  return { topics: topicsResponse, templates: templatesResponse };
};

/**
 * Deletes a topic by ID.
 * @param {string} topicId - The ID of the topic to delete.
 * @throws {Error} If the deletion fails.
 */
export const deleteTopic = async (topicId) => {
  await TemplatesApi.deleteTopic(topicId);
};

/**
 * Deletes a subtopic by ID.
 * @param {string} subtopicId - The ID of the subtopic to delete.
 * @throws {Error} If the deletion fails.
 */
export const deleteSubtopic = async (subtopicId) => {
  await TemplatesApi.deleteSubtopic(subtopicId);
};

/**
 * Filters templates based on provided filters.
 * @param {Array} templates - The full list of templates.
 * @param {Object} filters - The filter criteria.
 * @returns {Array} The filtered templates.
 */
export const filterTemplates = (templates, filters) => {
  let filtered = templates;
  if (filters.postType && filters.postType !== 'all') {
    filtered = filtered.filter(
      (template) => template.category === filters.postType
    );
  }
  if (filters.topic && filters.topic !== 'all') {
    filtered = filtered.filter(
      (template) => template.topic.id === filters.topic
    );
  }
  if (filters.subtopic && filters.subtopic !== 'all') {
    filtered = filtered.filter(
      (template) => template.subtopic.id === filters.subtopic
    );
  }
  return filtered;
};

/**
 * Searches templates by message content.
 * @param {Array} templates - The list of templates to search.
 * @param {string} searchQuery - The search term.
 * @returns {Array} The filtered templates.
 */
export const searchTemplates = (templates, searchQuery) => {
  const normalizedQuery = normalizeString(searchQuery);
  return templates.filter(({ message }) =>
    normalizeString(message).includes(normalizedQuery)
  );
};

/**
 * Normalizes a string for case-insensitive, accent-insensitive search.
 * @param {string} str - The string to normalize.
 * @returns {string} The normalized string.
 */
const normalizeString = (str) => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

/**
 * Gets the count of active templates for a topic or subtopic with an end_date in the future or no end_date (evergreen).
 * @param {Array} templates - The full list of templates.
 * @param {string} id - The topic or subtopic ID.
 * @param {string} type - 'topic' or 'subtopic'.
 * @returns {number} The number of active templates.
 */
export const getActiveTemplateCount = (templates, id, type) => {
  const now = new Date();
  return templates.filter((template) => {
    const isMatching =
      type === 'topic'
        ? template.topic?.id === id
        : template.subtopic?.id === id;
    const endDate = template.end_date ? new Date(template.end_date) : null;
    return isMatching && (!endDate || endDate > now);
  }).length;
};

/**
 * Ends all active templates for a given topic or subtopic by setting end_date to now.
 * @param {Array} templates - The full list of templates.
 * @param {string} id - The topic or subtopic ID.
 * @param {string} type - 'topic' or 'subtopic'.
 * @returns {Promise<void>} - Resolves when templates are ended.
 */
export const endActiveTemplatesForTopicOrSubtopic = async (
  templates,
  id,
  type
) => {
  const activeTemplateIds = templates
    .filter((template) => {
      const isMatching =
        type === 'topic'
          ? template.topic?.id === id
          : template.subtopic?.id === id;
      const endDate = template.end_date ? new Date(template.end_date) : null;
      return isMatching && (!endDate || endDate > new Date());
    })
    .map((template) => template.id);

  if (activeTemplateIds.length === 0) {
    throw new Error('No active templates to end.');
  }

  await TemplatesApi.endTemplates(activeTemplateIds);
};
